import { useImperativeHandle, forwardRef, useEffect } from 'react'
import { useLocation, useHistory } from 'react-router'
import classNames from 'classnames'
import isMobile from 'shared/responsive-helpers'
import { SEARCH_MODE_AIRPORTS } from 'search/constants'
import styles from './index.module.scss'

export const Tab = ({ children, className = '' }) => (
    <div className={classNames(styles.tabsContent, styles[className])}>{children}</div>
)

const Tabs = forwardRef(
    (
        {
            children,
            className,
            tabChangeCallback,
            tabClickHandler,
            tabStyle = 'tabs',
            contentStyle = 'tab',
            activeTab = 0,
        },
        ref
    ) => {
        const [isActiveTab, setIsActiveTab] = React.useState(activeTab)
        const { hash } = useLocation()
        const history = useHistory()

        // function converts tab label into hash
        const getHashFromLabel = label => {
            if (label) {
                return `#${label.toLowerCase().trim().replaceAll(' ', '-')}`
            }
        }
        //this function checks if label is a string (at some pages we have label that is object)
        //depends on it labels for hashes are located in different levels and properties of an object
        const isLabelAString = label => typeof label === 'string'

        //we have at least 3 types of tabs object, they have different structure so we need to check it before rendering
        const isCorrectTabContentObj = children.every(child => child.key)

        ///check if tab's converted label === hash(because there can be some typo in url so we need to be sure) and return this object
        const findCurrentLabel = children.find(child =>
            isLabelAString(child.props.label)
                ? getHashFromLabel(child.props.label) === hash
                : getHashFromLabel(child.key) === hash
        )
        useImperativeHandle(ref, () => ({
            click: index => onClickHandler(index),
        }))

        const onClickHandler = (index, onClick) => {
            if (tabClickHandler) {
                const result = tabClickHandler()

                if (!result) return
            }

            tabChangeCallback && tabChangeCallback(index)
            onClick && onClick()

            if (index === 0 && children[index].key === SEARCH_MODE_AIRPORTS) {
                return history.push('/airports')
            }
            if (children[index].key) {
                return isLabelAString(children[index].props.label)
                    ? history.push(getHashFromLabel(children[index].props.label))
                    : history.push(getHashFromLabel(children[index].key))
            } else setIsActiveTab(index)
        }

        useEffect(() => {
            if (isCorrectTabContentObj && findCurrentLabel) {
                const newTabIndex = children.findIndex(child => child.key === findCurrentLabel.key)
                return setIsActiveTab(newTabIndex)
            }

            if (isActiveTab === -2 || isActiveTab === -1) {
                return setIsActiveTab(activeTab)
            }
        }, [hash])

        return (
            <div
                className={classNames(
                    !!tabStyle && styles[`tabs${tabStyle}`],
                    className && styles[`tabs${className}`]
                )}>
                <>
                    <ul className={styles.tabsNav}>
                        {React.Children.map(children, (child, index) => (
                            <li
                                onClick={() => onClickHandler(index, child.props.onClick)}
                                key={index}
                                className={classNames(
                                    styles.tabsNavItem,
                                    index === isActiveTab && styles.tabsNavItemActive
                                )}>
                                <span className={styles.tabsNavLabel}>{child.props.label}</span>
                                <span className={styles.tabsNavLabelIcon}> {child.props.icon}</span>
                                {isMobile() && contentStyle === 'tab' && index === isActiveTab && (
                                    <div className={styles.tabsContent}>
                                        {React.Children.map(children, (child, index) =>
                                            index === isActiveTab ? <Tab {...child.props} /> : null
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                    {(!isMobile() || contentStyle === 'separate') && (
                        <div className={styles.tabsContent}>
                            {React.Children.map(children, (child, index) =>
                                index === isActiveTab ? <Tab {...child.props} /> : null
                            )}
                        </div>
                    )}
                </>
            </div>
        )
    }
)

export default Tabs

Tabs.propTypes = {
    tabStyle: PropTypes.oneOf(['tabs', 'pills']),
    contentStyle: PropTypes.oneOf(['tab', 'separate']),
    activeTab: PropTypes.number,
    className: PropTypes.string,
}
